import { Paper, alpha, Icon, IconButton, makeStyles, Theme } from '@material-ui/core';
import { TreeItem, TreeView } from '@material-ui/lab';
import { FontIcon } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useMemo } from 'react';
import { EntitiesListingDataItem } from '.';
import { EntityNode } from '../../containers';
import { ReadonlyNonEmptyArray } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display      : 'flex',
            flexDirection: 'column',
            padding      : theme.spacing(1),
        },

        dataTree: {
            flex: 1,
        },

        dataTreeItem: {
            '& .MuiTreeItem-label': {
                paddingTop   : theme.spacing(0.5),
                paddingBottom: theme.spacing(0.5),
                whiteSpace   : 'nowrap',
            },

            '& .MuiTreeItem-group': {
                marginLeft : 7,
                paddingLeft: 18,
                borderLeft : `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
            },

            '&.load-more': {
                '& .MuiTreeItem-label': {
                    fontStyle : 'italic',
                    fontWeight: 'bold',
                },
            },

            '&.disabled': {
                color: theme.palette.text.disabled,
            },
        },

        dataTreeItemLabel: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            '& .label'   : {
                flex        : 1,
                paddingRight: theme.spacing(2),
            },
            '& .icon': {
                visibility: 'hidden',
            },
            '&:hover, :active': {
                '& .icon': {
                    visibility: 'visible',
                },
            },
        },

        toggleIcon: {
            fontWeight: 100,
        },

        [theme.breakpoints.up('md')]: {
            root: {
                padding: theme.spacing(2),
            },
        },


        [theme.breakpoints.up('lg')]: {
            root: {
                padding: theme.spacing(3),
            },
        },
    };
}, { name: 'EntitiesListingTreeViewComp', index: 1 });



/**
 * Given an array of entity node wrapper and an entityId, find and return the entity node wrapper that contain an entity with the same
 * entityId. This function will also recursively search through the entities' children and children's children and so on.
 */
const findEntityById = (
    entityDataItems: readonly EntitiesListingDataItem [] | undefined,
    entityId: string,
): EntitiesListingDataItem | undefined => {

    if (entityDataItems) {
        for (let i = 0; i < entityDataItems.length; i += 1) {
            const entityDataItem = entityDataItems[i];
            if (entityDataItem.actualValue.entity.entityId === entityId) {
                return entityDataItem;
            }
            const result = findEntityById(entityDataItem.displayValue.children, entityId);
            if (result) {
                return result;
            }
        }
    }
    return undefined;
};


/**
 * Given en entityId, return a nodeId used for TreeItem by combining the entityId with the node's level
 */
const encodeNodeId = (entityId: string, level: number): string => {
    return `${level}::${entityId}`;
};


/**
 * Given a tree item node id, return the entityId by removing the level number from the nodeId
 */
const decodeNodeId = (nodeId: string): string => {
    return nodeId.substring(nodeId.indexOf('::') + 2);
};



/**
 * The props required by the EntitiesListingTreeViewComp components.
 */
export interface EntitiesListingTreeViewCompProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    // The array of entities data items.
    readonly listData: ReadonlyNonEmptyArray<EntitiesListingDataItem>;

    // The entityId of the current selected entity.
    readonly selectedEntityId?: string | undefined;

    // Whether or not to display the root entity node or just start with displaying the root's children
    readonly showRootEntity?: boolean | undefined;

    readonly onEntitySelected?: ((entityNode: EntityNode)=> void) | undefined;
    readonly onPreviewItemClicked?: ((entityNode: EntityNode)=> void) | undefined;

    readonly expandedNodeIds: string[];
    readonly onExpandedNodeIdsChanged?: ((nodeIds: string[])=> void) | undefined;
    readonly hideExpandCollapseIcon?: boolean | undefined;
}


/**
 * This is a component that display list of entities. Right now it will automatically display entities as a Tree but we might add
 * or view e.g. List, in the future.
 */
export const EntitiesListingTreeViewComp: React.FC<EntitiesListingTreeViewCompProps> = (props: EntitiesListingTreeViewCompProps) => {
    const classes = useStyle();


    const rootEntityNode = useMemo(() => {
        return props.listData[0];
    }, [props.listData]);


    /**
     * On tree item label click, we will prevent default to prevent the tree item from expanding.
     */
    const onTreeItemLabelClicked = useCallback((event: React.ChangeEvent<{}>, nodeId: string) => {
        if (props.expandedNodeIds.includes(nodeId)) {
            // If the node that the user clicked on is one of the currently expanded nodes, stop the event so that the
            // node doesn't get collapsed. It is kind of annoying sometime that a node will collapse because the user want to select it.
            event.preventDefault();
        }
    }, [props.expandedNodeIds]);


    /**
     * When the list of expanded node changed, store it locally and pass it back to the TreeView
     */
    const onExpandedNodeIdsChanged = useCallback((event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        if (props.onExpandedNodeIdsChanged) {
            props.onExpandedNodeIdsChanged(nodeIds);
        }
    }, [props]);



    /**
     * On tree item selected, dispatch an event to notify the handler. NOTE that this event will be fired when the
     * user click on the icon or the label. We don't want to dispatch an event when the user click on the icon to expand/collapse
     * the tree therefore we must check to see what the user clicked on before dispatching the event.
     */
    const onTreeItemSelected = useCallback((event: React.ChangeEvent<{}>, nodeId: string) => {
        const entityId = decodeNodeId(nodeId);
        event.persist();

        // Find the entity item object by the entityId
        const selectedEntityItem = findEntityById(props.listData, entityId);

        // make sure this event is not triggered by clicking on the expand/collapse icon. If the event is triggered by clicking on the icon
        // then ignore the event UNLESS we have not fetched children for this node yet. If we have not fetched children for the node then
        // fetch the children once.
        const isIconClicked = (event.target as HTMLElement).closest('.MuiTreeItem-iconContainer');
        if (!isIconClicked || (isIconClicked && selectedEntityItem?.actualValue.numberOfPagesFetched === 0)) {
            if (props.onEntitySelected && selectedEntityItem) {
                props.onEntitySelected(selectedEntityItem.actualValue);
            }

            // Add the selected nodeId to the list of expandedNodeIds array if it is not already in the array
            // This will make this node automatically expanded when the user click on it.
            if (!props.expandedNodeIds.includes(nodeId) && props.onExpandedNodeIdsChanged) {
                props.onExpandedNodeIdsChanged(
                    [...props.expandedNodeIds, nodeId],
                );
            }
        }
    }, [props]);


    /**
     * Create <TreeItem /> element for 1 entity node item. If this node has children, we will recursively create 1 <TreeItem />
     * for each child.
     */
    const createTreeItemForSingleChild = useCallback((
        entityItem: EntitiesListingDataItem | undefined,
        level: number,
    ): JSX.Element => {
        if (!entityItem) {
            return <></>;
        }

        const treeItemId = encodeNodeId(entityItem.actualValue.entity.entityId, level);
        return (
            <TreeItem
                className={clsx(classes.dataTreeItem, entityItem.isLoadMoreItem && 'load-more', entityItem.actualValue.disabled && 'disabled')}
                key={treeItemId}
                nodeId={treeItemId}
                label={(
                    <div className={classes.dataTreeItemLabel}>
                        <div className="label">{entityItem.displayValue.name}</div>

                        {/* Show the preview button if the container has onPreviewItemClicked handler */}
                        {!entityItem.isLoadMoreItem && props.onPreviewItemClicked && (
                            <IconButton
                                className="preview icon"
                                size="small"
                                onClick={(event: React.MouseEvent) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (props.onPreviewItemClicked) {
                                        props.onPreviewItemClicked(entityItem.actualValue);
                                    }
                                }}
                            >
                                <Icon fontSize="small">visibility</Icon>
                            </IconButton>
                        )}
                    </div>
                )}
                onLabelClick={(event) => {
                    onTreeItemLabelClicked(event, treeItemId);
                }}
            >
                {entityItem.displayValue.children && entityItem.displayValue.children.length > 0 && (
                    entityItem.displayValue.children.map((child) => {
                        return createTreeItemForSingleChild(child, level + 1);
                    })
                )}
            </TreeItem>
        );
    }, [classes.dataTreeItem, classes.dataTreeItemLabel, onTreeItemLabelClicked, props]);

    return (
        <Paper
            elevation={2}
            className={clsx(props.className, classes.root)}
            style={props.style}
        >
            <TreeView
                className={classes.dataTree}
                defaultCollapseIcon={
                    props.hideExpandCollapseIcon ? <></> : <FontIcon className={classes.toggleIcon} iconName="fas fa-minus-square" />
                }
                defaultExpandIcon={props.hideExpandCollapseIcon ? <></> : <FontIcon className={classes.toggleIcon} iconName="fas fa-plus-square" />}
                onNodeToggle={onExpandedNodeIdsChanged}
                onNodeSelect={onTreeItemSelected}
                selected={props.selectedEntityId ?? ''}
                expanded={props.expandedNodeIds}
            >
                {props.showRootEntity ? (
                    createTreeItemForSingleChild(rootEntityNode, 0)
                ) : (
                    rootEntityNode?.displayValue.children?.map((child) => {
                        return createTreeItemForSingleChild(child, 0);
                    })
                )}
            </TreeView>
        </Paper>
    );
};
